
import { Account, WebApi } from "@/web-client";
import { Auth, Hub } from "aws-amplify";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

/** アカウントコンテキスト */
export interface AccountContext {
    /** アカウント情報。未ログインの場合は undefined. */
    account?: MyAccount;
    /** ログイン中かどうか */
    isLoggedIn: boolean;
    /** 変更 */
    onChanged: (account?: MyAccount) => void, 
}

/** アカウント情報 */
export interface MyAccount {
    /** アカウントID */
    id: string;

    /** メールアドレス */
    email: string;

    /** 名前 */
    name: string;

    /** アバター画像 */
    avaterImage?: string;

    /** 確認済みのチュートリアル */
    confirmedTutorial?: number
}

/** AmplifyのAuthユーザー情報 */
interface AmplifyAuthUserInfo {
    /** federated ID */
    id: string;
    /** User pool ID */
    username: string;
    /** 属性 */
    attributes: {
        /** Email */
        email: string,
    }
}

interface AmplifyHubData {
    payload: {
        event: string
    }
}

/**
 * アカウント情報を使用するコンテキスト
 */
export function useAccount () {
    const context = useContext(AccountContext);
    
    return {
        forceReloadAccount: (account?: MyAccount) => {
            context.onChanged(account);
        },
        isLoggedIn: context.isLoggedIn,
        account: context.account
    }
}

/**
 * アカウントコンテキスト
 */
export const AccountContext = React.createContext<AccountContext>({
    isLoggedIn: false,
    onChanged: () => {}
});

/**
 * アカウントコンテキストプロバイダー
 * @param props プロパティ
 */
 export const AccountContextProvider: React.FC = (props) => {

    const [accountInfo, setAccountInfo] = useState<AccountContext>();
    const history = useHistory()
    const onChanged = useCallback((account?: MyAccount) => {
        if (account) {
            setAccountInfo({
                isLoggedIn: true,
                account,
                onChanged
            });
            return;
        }
        initAsync()
    }, []);

    useEffect(() => {
        initAsync(); 

        const handler = (data?: AmplifyHubData) => {

            if (data?.payload.event === 'signOut') {
                setAccountInfo({
                    isLoggedIn: false,
                    onChanged
                })

                // GAのユーザーイベントを設定
                ReactGA.set({
                    userId: null
                })
            } else if (data?.payload.event === 'signIn') {
                // 既存のNolaユーザーがログインしたパターンに対応するためイベント処理をここで行わず、呼び出し元で forceReload する。
                //    initAsync();

                // GAのユーザーイベントを設定
                const username = (data.payload as unknown as any).data.username;
                ReactGA.set({
                    userId: username
                })
             
            } else if (data?.payload.event === 'configured') {
                // configure
            }
        }
        Hub.listen('auth', handler);

        return () => {
            Hub.remove('auth', handler);
        }       
    }, []);


    const initAsync = async () => {

        const userInfo = await Auth.currentUserInfo() as AmplifyAuthUserInfo;

        // userInfoはログイン時は値が入り、未ログイン時は nullとなる 
        if (!userInfo) {
            setAccountInfo({
                isLoggedIn: false,
                onChanged,
            })
            return;
        }

        setAccountInfo({
            isLoggedIn: true,
            onChanged
        })

        let account: Account;
        try {
            const result = await new WebApi().getAccount(e => {
                e.consume();
            });
            
            if (result.error) {
                if (result.error.response.status === 404) {
                    // アカウントが見つからない場合
                    if (history.location.pathname !== '/auth/related-service-account') {
                        history.push('/auth/related-service-account')
                    } 
                } else {
                    alert('不明なエラーが発生しました。\nお手数ですがページを更新してください。')
                }
                return;
            }
            account = result.result;
        } catch (e) {
            console.log('current:[' + history.location.pathname + ']');
            // if(e.response?.data.code === 1) {    // not found
            //     account = await new WebApiRequest().createAccount({
            //         email: userInfo.attributes.email,
            //         cognitoUserPoolUserId: userInfo.username
            //     })
            //     console.log('use created', account);
            // } else {
            //     throw new Error(e);
            // }
            // alert('not ')
            return;
        }

        if (!account.confirmedTutorial || account.confirmedTutorial! < 1) {
            history.push('/tutorial');
        }
        
        setAccountInfo({
            isLoggedIn: true,
            account: {
                id: userInfo.id,
                email: userInfo.attributes.email,
                name: account.name,
                avaterImage: account.avaterImage,
                confirmedTutorial: account.confirmedTutorial
            },
            onChanged
        })

        // GAにユーザーIDを設定
        ReactGA.set({
            userId: userInfo.username
        })
    }

    return (
        <AccountContext.Provider value={accountInfo!}>
            {
                accountInfo ? 
                    props.children : null
            }
        </AccountContext.Provider>
    );
}

